import React, { useState } from 'react';
import { bookingService } from '../../services/bookingService';
import { getTodayDate, formatDateForBackend, formatBackendDateForDisplay } from '../../services/dateUtils';
import Button from '../common/Button';
import PopupNotification from '../common/PopupNotification';

const VisitRequestForm = ({ wwooferId, hostId, threadId }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [agreedToDeclaration, setAgreedToDeclaration] = useState(false);
  const [showDeclarationPopup, setShowDeclarationPopup] = useState(false);

  const todayString = getTodayDate();

  const validateForm = () => {
    const newErrors = {};

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else if (formatDateForBackend(startDate) < todayString) {
      newErrors.startDate = 'Start date cannot be in the past';
    }

    if (!endDate) {
      newErrors.endDate = 'End date is required';
    } else if (formatDateForBackend(endDate) <= formatDateForBackend(startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (!agreedToDeclaration) {
      newErrors.agreedToDeclaration = 'You must agree to the Host Declaration';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert date input (YYYY-MM-DD) to DD-MM-YYYY for display
  const handleDateInputChange = (value, setterFunction) => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        setterFunction(`${day}-${month}-${year}`);
      }
    } else {
      setterFunction('');
    }
  };

  // Convert DD-MM-YYYY to YYYY-MM-DD for input value
  const getInputValue = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const parts = ddmmyyyy.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Convert dates from DD-MM-YYYY to YYYY-MM-DD for backend
      const data = await bookingService.createBooking({
        wwoofer_id: wwooferId,
        start_date: formatDateForBackend(startDate),
        end_date: formatDateForBackend(endDate),
        message: message,
      });

      if (data.success) {
        // Show popup notification instead of alert
        setPopupMessage(window.wwoofBooking?.popupTexts?.hostRequestSent || 'Your visit request has been sent!');
        setShowPopup(true);
        setStartDate('');
        setEndDate('');
        setMessage('');
        setAgreedToDeclaration(false);
        setErrors({});
      } else {
        setPopupMessage(data.message || 'Unknown error occurred');
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      setPopupMessage(error.message || 'Error creating booking request. Please try again.');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setPopupMessage('');
    // Only reload page if it was a success message (form was cleared)
    if (!startDate && !endDate && !message && !agreedToDeclaration) {
      window.location.reload();
    }
  };

  const handleShowDeclaration = (e) => {
    e.preventDefault();
    setShowDeclarationPopup(true);
  };

  const handleDeclarationPopupClose = () => {
    setShowDeclarationPopup(false);
  };

  return (
    <div className='wwoof-visit-request-form'>
      <form onSubmit={handleSubmit}>
        <div className='form-group'>
          <label htmlFor='start-date'>Start Date</label>
          <input
            type='date'
            id='start-date'
            value={getInputValue(startDate)}
            min={todayString}
            onChange={(e) => handleDateInputChange(e.target.value, setStartDate)}
            className={errors.startDate ? 'error' : ''}
          />
          {errors.startDate && (
            <span className='error-message'>{errors.startDate}</span>
          )}
          {startDate && (
            <div className='date-preview'>
              Display: {startDate}
            </div>
          )}
        </div>

        <div className='form-group'>
          <label htmlFor='end-date'>End Date</label>
          <input
            type='date'
            id='end-date'
            value={getInputValue(endDate)}
            min={getInputValue(startDate) || todayString}
            onChange={(e) => handleDateInputChange(e.target.value, setEndDate)}
            className={errors.endDate ? 'error' : ''}
          />
          {errors.endDate && (
            <span className='error-message'>{errors.endDate}</span>
          )}
          {endDate && (
            <div className='date-preview'>
              Display: {endDate}
            </div>
          )}
        </div>

        <div className='form-group'>
          <label htmlFor='message'>Message</label>
          <textarea
            id='message'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder='Add a personal message to your visit request...'
            rows={4}
          />
        </div>

        <div className='form-group'>
          <div className='checkbox-group'>
            <input
              type='checkbox'
              id='agreed-to-declaration'
              checked={agreedToDeclaration}
              onChange={(e) => setAgreedToDeclaration(e.target.checked)}
              className={errors.agreedToDeclaration ? 'error' : ''}
            />
            <label htmlFor='agreed-to-declaration'>
              I agree to the{' '}
              <button 
                type='button' 
                className='declaration-link'
                onClick={handleShowDeclaration}
              >
                Host Declaration
              </button>
            </label>
          </div>
          {errors.agreedToDeclaration && (
            <span className='error-message'>{errors.agreedToDeclaration}</span>
          )}
        </div>

        <Button 
          type='submit' 
          disabled={loading} 
          className='wwoof-submit-button'
        >
          {loading ? 'Sending...' : 'Request Visit'}
        </Button>
      </form>

      <PopupNotification 
        message={popupMessage}
        isVisible={showPopup}
        onClose={handlePopupClose}
      />

      <PopupNotification 
        message={window.wwoofBooking?.popupTexts?.hostDeclaration || '<h3>Host Declaration</h3><p>Please configure the Host Declaration text in the admin backend.</p>'}
        isVisible={showDeclarationPopup}
        onClose={handleDeclarationPopupClose}
      />
    </div>
  );
};

export default VisitRequestForm;
